import {
  CheckOutlined,
  CloseOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import {
  Form,
  Input,
  Button,
  Switch,
  Layout,
  Select,
  DatePicker,
  Space,
  Row,
  Col,
} from "antd";
import { useForm } from "antd/lib/form/Form";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import TranslateBtn from "../../components/product/TranslateBtn";
import ImageUpload from "../../components/media/ImageUpload";
import VideoUpload from "../../components/media/VideoUpload";
import { ApiManager, Locale, Util } from "../../utils";
import { getUserInfo } from "../../utils/redux/reducers/User";
import Base from "../Base";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();
const util = Util.getInstance();
const base = Base.getInstance();

const defaultVideoList = (v) => {
  if (!v) return [];
  if (typeof v === "string" && v) {
    return [
      {
        uid: -1,
        name: `video-${Date.now()}`,
        status: "done",
        url: v,
      },
    ];
  }
  return [];
};

function pickMediaPath(item) {
  if (!item) return "";
  return item.file_path || item.path || item.url || "";
}

function EditorsChoiceEdit() {
  const [form] = useForm();
  const [loading, setLoading] = useState(false);
  const [articles, setArticles] = useState([]);
  const user = useSelector((state) => getUserInfo(state));
  const router = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (!user.token) return;
    const apiUrl = api.article.list;
    (async () => {
      try {
        const res = await apiUrl.run({
          token: user.token,
          query: {
            page: 1,
            category_id: -1,
            count: 200,
            "filter[status]": -1,
          },
        });
        if (res.result) {
          setArticles((res.data && res.data.data) || []);
        }
      } catch (error) {
        base.handleApiError(apiUrl, error);
      }
    })();
  }, [user.token]);

  useEffect(() => {
    const { state } = location;
    if (state && state.data) {
      const d = state.data;
      form.setFieldsValue({
        article_id: d.article_id,
        show_on_date: d.show_on_date ? dayjs(d.show_on_date) : null,
        zh_comment: d.zh_comment,
        ug_comment: d.ug_comment,
        video: defaultVideoList(d.video_url),
        status: d.status === 1,
        zh_image: util.getDefaultImages(d.zh_image),
        ug_image: util.getDefaultImages(d.ug_image),
      });
    }
  }, [form, location]);

  async function onFinish(values) {
    const { token } = user;
    if (!token) return;

    const { zh_image, ug_image, status, show_on_date, video, ...otherValues } =
      values;
    let apiUrl = api.article.editorsChoiceAdd;
    const apiConfig = {
      token,
      data: {
        ...otherValues,
        status: status ? 1 : 0,
        show_on_date: show_on_date
          ? show_on_date.format("YYYY-MM-DD")
          : undefined,
      },
    };

    if (zh_image && zh_image.length > 0) {
      apiConfig.data.zh_image = pickMediaPath(zh_image[0]);
    } else {
      apiConfig.data.zh_image = "";
    }
    if (ug_image && ug_image.length > 0) {
      apiConfig.data.ug_image = pickMediaPath(ug_image[0]);
    } else {
      apiConfig.data.ug_image = "";
    }

    if (video && video.length > 0) {
      apiConfig.data.video_url = pickMediaPath(video[0]);
    } else {
      apiConfig.data.video_url = "";
    }

    const { state } = location;
    if (state && state.data) {
      apiUrl = api.article.editorsChoiceUpdate;
      apiConfig.path = { id: state.data.id };
    }

    try {
      setLoading(true);
      const res = await apiUrl.run(apiConfig);
      if (res.result) {
        base.showMessage(res.message, Base.Message.success);
        router.goBack();
      } else {
        base.showModal(res.message, Base.Modal.warning);
      }
    } catch (error) {
      base.handleApiError(apiUrl, error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      <Layout.Header style={{ backgroundColor: "#fff" }}>
        <Button type="link" onClick={() => router.goBack()}>
          <LeftOutlined />
          {i18n.t("productEdit.headerBack")}
        </Button>
      </Layout.Header>
      <Layout.Content>
        <Form
          labelCol={{ span: 3 }}
          wrapperCol={{ span: 10 }}
          form={form}
          onFinish={onFinish}
          style={{
            backgroundColor: "#fff",
            paddingTop: 20,
            paddingBottom: 20,
          }}
        >
          <Form.Item
            label={i18n.t("article.title")}
            name="article_id"
            rules={[
              { required: true, message: i18n.t("article.selectWarning") },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={articles.map((a) => ({
                value: a.id,
                label: `${i18n.getPropNameByLocale(a, "name")} (#${a.id})`,
              }))}
            />
          </Form.Item>
          <Form.Item
            label={i18n.t("article.showOnDate")}
            name="show_on_date"
            rules={[
              {
                required: true,
                message: i18n.t("article.showOnDateRequired"),
              },
            ]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                label={
                  <Space>
                    {i18n.t("article.zhComment")}
                    <TranslateBtn
                      context={getFieldValue("zh_comment")}
                      onFinish={(value) =>
                        setFieldsValue({ ug_comment: value })
                      }
                      from="zh"
                      to="ug"
                    />
                  </Space>
                }
                name="zh_comment"
                rules={[
                  {
                    required: true,
                    message: i18n.t("article.commentRequired"),
                  },
                ]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                label={
                  <Space>
                    {i18n.t("article.ugComment")}
                    <TranslateBtn
                      context={getFieldValue("ug_comment")}
                      onFinish={(value) =>
                        setFieldsValue({ zh_comment: value })
                      }
                      from="ug"
                      to="zh"
                    />
                  </Space>
                }
                name="ug_comment"
                rules={[
                  {
                    required: true,
                    message: i18n.t("article.commentRequired"),
                  },
                ]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            )}
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue, setFieldsValue }) => (
                  <Form.Item
                    name="ug_image"
                    label={i18n.t("sales.ad.ugImg")}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                  >
                    <ImageUpload
                      images={getFieldValue("ug_image")}
                      setSelectedImages={(image) => {
                        setFieldsValue({ ug_image: image });
                      }}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue, setFieldsValue }) => (
                  <Form.Item
                    name="zh_image"
                    label={i18n.t("sales.ad.zhImg")}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                  >
                    <ImageUpload
                      images={getFieldValue("zh_image")}
                      setSelectedImages={(image) => {
                        setFieldsValue({ zh_image: image });
                      }}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
          </Row>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item label={i18n.t("article.video")} name="video">
                <VideoUpload
                  video={getFieldValue("video")}
                  setSelectedVideo={(vid) => {
                    setFieldsValue({ video: vid });
                  }}
                  setDeleted={() => {
                    setFieldsValue({ video: [] });
                  }}
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item
            name="status"
            label={i18n.t("sales.status")}
            valuePropName="checked"
            initialValue={true}
          >
            <Switch
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </Form.Item>
          <Form.Item wrapperCol={{ offset: 2 }}>
            <Button
              disabled={loading}
              loading={loading}
              type="primary"
              htmlType="submit"
            >
              {i18n.t("sales.submit")}
            </Button>
          </Form.Item>
        </Form>
      </Layout.Content>
    </Layout>
  );
}

export default EditorsChoiceEdit;
